import React, { useEffect, useMemo, useState } from "react";
import * as blogService from "../../services/blogService";

const initialForm = {
  title: "",
  slug: "",
  excerpt: "",
  content_html: "",
  featured_image: "",
  status: "draft",
  is_featured: false,
  is_active: true,
  category_id: "",
  author_name: "",
  seo_title: "",
  seo_description: "",
  meta_title: "",
  meta_desc: "",
  og_title: "",
  og_description: "",
  og_twitter_card: "summary_large_image",
  og_type: "article",
  canonical_path: "",
  cover_alt: "",
  hero_bg: "",
  subtitle: "",
  reading_time: "",
  keywords: "",
  tags: "",
  faq_json: "[]",
  portfolio_json: "[]",
};

function makeSlug(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function normalizeJsonString(value, fallback = "[]") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (error) {
    return fallback;
  }
}

function normalizePayload(values) {
  return {
    ...values,
    category_id: values.category_id ? Number(values.category_id) : null,
    is_featured: Boolean(values.is_featured),
    is_active: Boolean(values.is_active),
    faq_json: normalizeJsonString(values.faq_json, "[]"),
    portfolio_json: normalizeJsonString(values.portfolio_json, "[]"),
  };
}

export default function BlogForm({
  initialData = null,
  categories = [],
  onSaved,
  onCancel,
}) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [localCategories, setLocalCategories] = useState([]);

  useEffect(() => {
    setLocalCategories(Array.isArray(categories) ? categories : []);
  }, [categories]);

  useEffect(() => {
    if (!initialData) {
      setForm(initialForm);
      return;
    }

    setForm({
      ...initialForm,
      ...initialData,
      category_id: initialData?.category_id || initialData?.category?.id || "",
      faq_json: normalizeJsonString(initialData?.faq_json, "[]"),
      portfolio_json: normalizeJsonString(initialData?.portfolio_json, "[]"),
    });
  }, [initialData]);

  const selectedCategory = useMemo(() => {
    return (
      localCategories.find(
        (item) => String(item.id) === String(form.category_id)
      ) || null
    );
  }, [localCategories, form.category_id]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleTitleChange(value) {
    setForm((prev) => {
      const next = { ...prev, title: value };
      if (!prev.slug || prev.slug === makeSlug(prev.title)) {
        next.slug = makeSlug(value);
      }
      return next;
    });
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "Blog");

      const result = await blogService.uploadMedia(formData);

      const imageUrl =
        result?.file_url ||
        result?.url ||
        result?.path ||
        result?.data?.file_url ||
        result?.data?.url ||
        "";

      if (imageUrl) {
        setForm((prev) => ({
          ...prev,
          featured_image: imageUrl,
          hero_bg: prev.hero_bg || imageUrl,
        }));
      }
    } catch (error) {
      alert(error?.message || "Gagal upload gambar blog.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function handleCreateCategory() {
    const name = newCategory.trim();
    if (!name) return;

    try {
      if (typeof blogService.createCategory !== "function") {
        alert(
          "Fitur tambah kategori blog belum tersedia di service. Tambahkan kategori dari halaman Categories."
        );
        return;
      }

      const payload = {
        name,
        slug: makeSlug(name),
        is_active: true,
        sort_order: localCategories.length + 1,
      };

      const created = await blogService.createCategory(payload);
      const newItem = created?.data || created;

      if (newItem?.id) {
        setLocalCategories((prev) => [...prev, newItem]);
        updateField("category_id", String(newItem.id));
        setNewCategory("");
      }
    } catch (error) {
      alert(error?.message || "Gagal membuat kategori blog.");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = normalizePayload(form);

      let result;
      if (initialData?.id) {
        result = await blogService.updateBlog(initialData.id, payload);
      } else {
        result = await blogService.createBlog(payload);
      }

      if (onSaved) onSaved(result);
    } catch (error) {
      alert(error?.message || "Gagal menyimpan blog.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 font-medium">Judul</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Slug</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.slug}
            onChange={(e) => updateField("slug", makeSlug(e.target.value))}
            required
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Kategori</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.category_id}
            onChange={(e) => updateField("category_id", e.target.value)}
          >
            <option value="">Pilih kategori</option>
            {localCategories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {selectedCategory ? (
            <p className="text-xs text-gray-500 mt-1">
              Kategori terpilih: {selectedCategory.name}
            </p>
          ) : null}
        </div>

        <div>
          <label className="block mb-1 font-medium">Tambah kategori cepat</label>
          <div className="flex gap-2">
            <input
              className="flex-1 border rounded-lg px-3 py-2"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Nama kategori baru"
            />
            <button
              type="button"
              onClick={handleCreateCategory}
              className="px-4 py-2 rounded-lg border"
            >
              Tambah
            </button>
          </div>
        </div>

        <div>
          <label className="block mb-1 font-medium">Author</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.author_name}
            onChange={(e) => updateField("author_name", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Status</label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => updateField("status", e.target.value)}
          >
            <option value="draft">draft</option>
            <option value="published">published</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block mb-1 font-medium">Excerpt</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
          value={form.excerpt}
          onChange={(e) => updateField("excerpt", e.target.value)}
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Konten HTML</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2 min-h-[260px] font-mono text-sm"
          value={form.content_html}
          onChange={(e) => updateField("content_html", e.target.value)}
          placeholder="<p>Tulis konten HTML di sini...</p>"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 font-medium">Featured image URL</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.featured_image}
            onChange={(e) => updateField("featured_image", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Upload featured image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="w-full border rounded-lg px-3 py-2"
          />
          {uploading ? (
            <p className="text-xs text-gray-500 mt-1">Mengunggah...</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 font-medium">SEO Title</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.seo_title}
            onChange={(e) => updateField("seo_title", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">SEO Description</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
            value={form.seo_description}
            onChange={(e) => updateField("seo_description", e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-1 font-medium">Meta Title</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.meta_title}
            onChange={(e) => updateField("meta_title", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Meta Description</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
            value={form.meta_desc}
            onChange={(e) => updateField("meta_desc", e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block mb-1 font-medium">Subtitle</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.subtitle}
            onChange={(e) => updateField("subtitle", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Reading Time</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.reading_time}
            onChange={(e) => updateField("reading_time", e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Keywords</label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            value={form.keywords}
            onChange={(e) => updateField("keywords", e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(form.is_featured)}
            onChange={(e) => updateField("is_featured", e.target.checked)}
          />
          Featured
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(form.is_active)}
            onChange={(e) => updateField("is_active", e.target.checked)}
          />
          Aktif
        </label>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-black text-white"
        >
          {saving ? "Menyimpan..." : initialData?.id ? "Update Blog" : "Tambah Blog"}
        </button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border"
          >
            Batal
          </button>
        ) : null}
      </div>
    </form>
  );
}